import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp, Eye, Play, Share2, Zap, Clock,
  ArrowRight, Upload, Calendar, Shield,
  ChevronUp, ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip as RechartTooltip, ResponsiveContainer
} from 'recharts';
import { useApp } from '@/context/AppContext';
import { statsApi, publishApi } from '@/services/api';

const VIEWS_DATA = [
  { day: 'Mon', views: 1200, engagement: 430 },
  { day: 'Tue', views: 1900, engagement: 710 },
  { day: 'Wed', views: 1500, engagement: 590 },
  { day: 'Thu', views: 2800, engagement: 980 },
  { day: 'Fri', views: 3200, engagement: 1200 },
  { day: 'Sat', views: 4100, engagement: 1580 },
  { day: 'Sun', views: 3600, engagement: 1340 },
];

const QUICK_ACTIONS = [
  { label: 'Upload Video', icon: Upload, desc: 'Add to Drive', path: '/drive', color: 'hsl(var(--primary))' },
  { label: 'Create Reel', icon: Play, desc: 'Reels Creator', path: '/create?tab=reels', color: 'hsl(var(--accent))' },
  { label: 'Schedule Post', icon: Calendar, desc: 'Publishing Hub', path: '/publish', color: 'hsl(var(--success))' },
  { label: 'Risk Scan', icon: Shield, desc: 'Check content', path: '/risk', color: 'hsl(var(--warning))' },
];

function fmtNum(n) {
  if (!n && n !== 0) return '—';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
}

function timeAgo(iso) {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

const StatusBadge = ({ status }) => {
  const map = {
    published: { label: 'Published', bg: 'hsl(var(--success) / 0.12)', color: 'hsl(var(--success))', border: 'hsl(var(--success) / 0.25)' },
    scheduled: { label: 'Scheduled', bg: 'hsl(var(--primary) / 0.12)', color: 'hsl(var(--primary))', border: 'hsl(var(--primary) / 0.25)' },
    processing: { label: 'Processing', bg: 'hsl(var(--warning) / 0.12)', color: 'hsl(var(--warning))', border: 'hsl(var(--warning) / 0.25)' },
    draft: { label: 'Draft', bg: 'hsl(var(--muted))', color: 'hsl(var(--foreground-muted))', border: 'hsl(var(--border))' },
  };
  const s = map[status] || map.draft;
  return (
    <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>
      {s.label}
    </span>
  );
};

const PlatformDot = ({ platform }) => {
  const colors = { instagram: '#E1306C', tiktok: '#69C9D0', youtube: '#FF0000', x: '#9AA4B2', facebook: '#1877F2' };
  return <span className="text-xs font-semibold" style={{ color: colors[platform] || '#9AA4B2' }}>{platform.toUpperCase().slice(0,2)}</span>;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload) return null;
  return (
    <div className="reelia-card px-3 py-2" style={{ padding: '0.5rem 0.75rem' }}>
      <p className="text-xs font-medium mb-1" style={{ color: 'hsl(var(--foreground))' }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs" style={{ color: p.color }}>{p.name}: {p.value.toLocaleString()}</p>
      ))}
    </div>
  );
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { activeBrand, activeCreator, allBrands, trialDaysLeft, trialActive } = useApp();

  const [stats, setStats] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);
  const [statsLoading, setStatsLoading] = useState(false);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const [statsData, postsData] = await Promise.all([
        statsApi.get({
          creatorId: activeCreator?.id,
          brandId: activeBrand?.id,
        }),
        activeBrand?.id
          ? publishApi.listPosts({ brandId: activeBrand.id })
          : Promise.resolve([]),
      ]);
      setStats(statsData);
      // Show last 5 posts sorted by scheduled_at desc
      const sorted = [...postsData].sort(
        (a, b) => new Date(b.scheduled_at) - new Date(a.scheduled_at)
      );
      setRecentPosts(sorted.slice(0, 5));
    } catch (e) {
      console.warn('Stats load error', e);
    } finally {
      setStatsLoading(false);
    }
  }, [activeBrand?.id, activeCreator?.id]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const getBrandName = (brandId) =>
    allBrands.find(b => b.id === brandId)?.name || brandId || '—';

  const statCards = [
    {
      label: 'Total Views',
      value: stats ? fmtNum(stats.total_views) : '—',
      change: stats ? `${stats.total_videos} video${stats.total_videos !== 1 ? 's' : ''}` : '...',
      up: true,
      icon: Eye,
    },
    {
      label: 'Posts Published',
      value: stats ? String(stats.published_posts) : '—',
      change: stats ? `${stats.total_posts} total` : '...',
      up: true,
      icon: Share2,
    },
    {
      label: 'Engagement Rate',
      value: stats ? `${stats.engagement_rate}%` : '—',
      change: 'Last 7 days',
      up: true,
      icon: TrendingUp,
    },
    {
      label: 'Viral Score Avg',
      value: stats ? String(stats.viral_score_avg) : '—',
      change: stats ? `${stats.reel_projects} reel${stats.reel_projects !== 1 ? 's' : ''}` : '...',
      up: true,
      icon: Zap,
    },
  ];

  const fadeUp = (delay = 0) => ({
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.35, delay, ease: 'easeOut' }
  });

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Trial banner */}
      {trialActive && (
        <motion.div {...fadeUp(0)}
          className="flex items-center justify-between p-3 rounded-lg"
          style={{ background: 'hsl(var(--primary) / 0.08)', border: '1px solid hsl(var(--primary) / 0.2)' }}
        >
          <div className="flex items-center gap-2">
            <Clock size={14} style={{ color: 'hsl(var(--primary))' }} />
            <span className="text-sm" style={{ color: 'hsl(var(--foreground))' }}>
              <strong>{trialDaysLeft} days</strong> left in your free trial
            </span>
            <Badge className="badge-blue text-xs rounded-full border">Watermark active</Badge>
          </div>
          <Button size="sm" className="text-xs h-7" onClick={() => navigate('/billing')}
            style={{ background: 'hsl(var(--primary))', color: '#fff' }}
          >
            Upgrade Now
          </Button>
        </motion.div>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, i) => (
          <motion.div key={stat.label} {...fadeUp(i * 0.06)} className="reelia-card-glow p-4 flex flex-col gap-3"
            data-testid={`stat-card-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs" style={{ color: 'hsl(var(--foreground-muted))' }}>{stat.label}</span>
              <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: 'hsl(var(--primary) / 0.1)' }}>
                <stat.icon size={14} style={{ color: 'hsl(var(--primary))' }} />
              </div>
            </div>
            <div>
              <p className="text-2xl font-semibold" style={{ color: 'hsl(var(--foreground))' }}>
                {statsLoading ? <span style={{ color: 'hsl(var(--foreground-subtle))' }}>...</span> : stat.value}
              </p>
              <div className="flex items-center gap-1 mt-0.5">
                {stat.up
                  ? <ChevronUp size={12} style={{ color: 'hsl(var(--success))' }} />
                  : <ChevronDown size={12} style={{ color: 'hsl(var(--destructive))' }} />}
                <span className="text-xs" style={{ color: stat.up ? 'hsl(var(--success))' : 'hsl(var(--destructive))' }}>
                  {stat.change}
                </span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Chart + Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Chart */}
        <motion.div {...fadeUp(0.15)} className="lg:col-span-2 reelia-card p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold" style={{ color: 'hsl(var(--foreground))' }}>Performance Overview</h3>
              <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--foreground-muted))' }}>Last 7 days across all brands</p>
            </div>
            <select className="text-xs rounded-md px-2 py-1" style={{ background: 'hsl(var(--surface))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--foreground-muted))' }}>
              <option>Last 7 days</option>
              <option>Last 30 days</option>
            </select>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={VIEWS_DATA}>
              <defs>
                <linearGradient id="viewsGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(216, 95%, 60%)" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="hsl(216, 95%, 60%)" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="engGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(186, 80%, 52%)" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="hsl(186, 80%, 52%)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: 'hsl(220, 10%, 55%)' }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <RechartTooltip content={<CustomTooltip />} cursor={{ stroke: 'hsl(220, 14%, 17%)', strokeWidth: 1 }} />
              <Area type="monotone" dataKey="views" name="Views" stroke="hsl(216, 95%, 60%)" fill="url(#viewsGrad)" strokeWidth={2} dot={false} />
              <Area type="monotone" dataKey="engagement" name="Engagement" stroke="hsl(186, 80%, 52%)" fill="url(#engGrad)" strokeWidth={2} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
          <div className="flex items-center gap-4 mt-2">
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-0.5 rounded" style={{ background: 'hsl(var(--primary))' }} />
              <span className="text-xs" style={{ color: 'hsl(var(--foreground-muted))' }}>Views</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-0.5 rounded" style={{ background: 'hsl(var(--accent))' }} />
              <span className="text-xs" style={{ color: 'hsl(var(--foreground-muted))' }}>Engagement</span>
            </div>
          </div>
        </motion.div>

        {/* Quick Actions */}
        <motion.div {...fadeUp(0.18)} className="reelia-card p-4">
          <h3 className="text-sm font-semibold mb-3" style={{ color: 'hsl(var(--foreground))' }}>Quick Actions</h3>
          <div className="grid grid-cols-2 gap-2">
            {QUICK_ACTIONS.map(qa => (
              <button
                key={qa.label}
                onClick={() => navigate(qa.path)}
                className="flex flex-col items-start gap-2 p-3 rounded-lg"
                style={{ background: 'hsl(var(--surface))', border: '1px solid hsl(var(--border))' }}
              >
                <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: `${qa.color}18` }}>
                  <qa.icon size={15} style={{ color: qa.color }} />
                </div>
                <div>
                  <p className="text-xs font-semibold" style={{ color: 'hsl(var(--foreground))' }}>{qa.label}</p>
                  <p className="text-xs" style={{ color: 'hsl(var(--foreground-subtle))' }}>{qa.desc}</p>
                </div>
              </button>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Recent Content */}
      <motion.div {...fadeUp(0.22)} className="reelia-card">
        <div className="flex items-center justify-between p-5 pb-3">
          <h3 className="text-sm font-semibold" style={{ color: 'hsl(var(--foreground))' }}>Recent Content</h3>
          <Button variant="ghost" size="sm" className="text-xs gap-1.5" onClick={() => navigate('/publish')}
            style={{ color: 'hsl(var(--primary))' }}
          >
            View all <ArrowRight size={12} />
          </Button>
        </div>
        <div className="overflow-x-auto">
          {recentPosts.length === 0 ? (
            <div className="px-5 py-8 text-center">
              <p className="text-sm" style={{ color: 'hsl(var(--foreground-muted))' }}>
                {statsLoading ? 'Loading...' : 'No posts scheduled yet'}
              </p>
              {!statsLoading && (
                <Button size="sm" variant="ghost" className="mt-2 text-xs gap-1"
                  onClick={() => navigate('/publish')}
                  style={{ color: 'hsl(var(--primary))' }}>
                  Schedule your first post <ArrowRight size={11} />
                </Button>
              )}
            </div>
          ) : (
            <table className="w-full" data-testid="recent-content-table">
              <thead>
                <tr style={{ borderBottom: '1px solid hsl(var(--border))' }}>
                  {['Title', 'Brand', 'Platform', 'Status', 'Scheduled'].map(h => (
                    <th key={h} className="text-left px-5 py-2 text-xs font-medium"
                      style={{ color: 'hsl(var(--foreground-subtle))' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recentPosts.map((item, i) => (
                  <tr key={item.id} className="hover:bg-surface-hover cursor-pointer"
                    style={{ borderBottom: i < recentPosts.length - 1 ? '1px solid hsl(var(--border-muted))' : 'none' }}
                    onClick={() => navigate('/publish')}
                  >
                    <td className="px-5 py-3">
                      <p className="text-sm font-medium" style={{ color: 'hsl(var(--foreground))' }}>{item.title}</p>
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-xs" style={{ color: 'hsl(var(--foreground-muted))' }}>
                        {getBrandName(item.brand_id)}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <PlatformDot platform={item.platform} />
                    </td>
                    <td className="px-5 py-3">
                      <StatusBadge status={item.status} />
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-xs" style={{ color: 'hsl(var(--foreground-subtle))' }}>
                        {timeAgo(item.scheduled_at)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </motion.div>
    </div>
  );
}
